import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, isValid, parse } from 'date-fns'
import { ko, enUS } from '@quebon/ui/lib/calendar-locale'
import { CalendarDays, ChevronDown, X } from 'lucide-react'
import { Calendar } from '@quebon/ui/components/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@quebon/ui/components/popover'
import { cn } from '@quebon/ui/lib/utils'

const DATE_FORMAT = 'yyyy-MM-dd'
const MIN_YEAR = 1900
const YEARS_BACK = 30
const DEFAULT_AGE_YEARS = 8

type Panel = 'year' | 'month' | null

interface Props {
  /** ISO date string (YYYY-MM-DD) or '' when empty/invalid */
  value: string
  onChange: (value: string) => void
  id?: string
  'aria-label'?: string
  required?: boolean
  /** Applied to the text input so each host page keeps its own field styling */
  inputClassName?: string
}

/** Format free-typed digits into YYYY-MM-DD as the user types. */
export function formatBirthDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  let out = digits.slice(0, 4)
  if (digits.length > 4) out += `-${digits.slice(4, 6)}`
  if (digits.length > 6) out += `-${digits.slice(6, 8)}`
  return out
}

/** Parse a YYYY-MM-DD string into a Date when it is a real, non-future date. */
export function parseBirthDate(text: string, today: Date = new Date()): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null
  const parsed = parse(text, DATE_FORMAT, today)
  if (!isValid(parsed)) return null
  if (format(parsed, DATE_FORMAT) !== text) return null
  if (parsed.getFullYear() < MIN_YEAR) return null
  if (parsed > today) return null
  return parsed
}

function monthLabel(monthIndex: number, lang: string): string {
  return new Date(2000, monthIndex, 1).toLocaleString(lang, { month: 'short' })
}

interface CaptionContextValue {
  lang: string
  openPanel: (panel: Panel) => void
  yearLabel: string
  monthLabelText: string
}

const CaptionContext = createContext<CaptionContextValue | null>(null)

/**
 * Replaces react-day-picker's caption. Native <select> dropdowns inside a popover
 * dismiss immediately on iOS Safari, so year/month pickers open as inline panels instead.
 */
function PickerCaption({ calendarMonth }: { calendarMonth: { date: Date } }) {
  const ctx = useContext(CaptionContext)
  const { date } = calendarMonth
  if (!ctx) return <div className="h-(--cell-size)" />
  const buttonClass =
    'flex h-7 items-center gap-0.5 rounded-md px-1.5 text-sm font-medium text-foreground hover:bg-muted'
  // react-day-picker's nav (prev/next) is absolutely positioned over the caption row, so the
  // caption must be a positioned layer above it for pointer clicks to reach these buttons.
  return (
    <div className="relative z-10 flex h-(--cell-size) w-fit mx-auto items-center justify-center gap-1">
      <button type="button" className={buttonClass} aria-label={ctx.yearLabel} onClick={() => ctx.openPanel('year')}>
        {date.getFullYear()}
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </button>
      <button
        type="button"
        className={buttonClass}
        aria-label={ctx.monthLabelText}
        onClick={() => ctx.openPanel('month')}
      >
        {monthLabel(date.getMonth(), ctx.lang)}
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </button>
    </div>
  )
}

const calendarComponents = { MonthCaption: PickerCaption }

export default function BirthDatePicker({
  value,
  onChange,
  id,
  'aria-label': ariaLabel,
  required,
  inputClassName,
}: Props) {
  const { t, i18n } = useTranslation('common')
  const [text, setText] = useState(value)
  const [open, setOpen] = useState(false)
  const [panel, setPanel] = useState<Panel>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const committedRef = useRef(value)

  // Sync from parent when the value changes externally (edit form load, reset).
  useEffect(() => {
    if (value !== committedRef.current) {
      committedRef.current = value
      setText(value)
    }
  }, [value])

  const today = new Date()
  const selected = parseBirthDate(value, today) ?? undefined
  const invalid = text.length > 0 && parseBirthDate(text, today) === null
  const defaultMonth = selected ?? new Date(today.getFullYear() - DEFAULT_AGE_YEARS, 0, 1)
  const [month, setMonth] = useState<Date>(defaultMonth)

  useEffect(() => {
    inputRef.current?.setCustomValidity(invalid ? t('datePicker.invalid') : '')
  }, [invalid, t])

  const commit = (next: string) => {
    committedRef.current = next
    onChange(next)
  }

  const handleTextChange = (raw: string) => {
    const formatted = formatBirthDateInput(raw)
    setText(formatted)
    const parsed = parseBirthDate(formatted, today)
    const next = parsed ? formatted : ''
    if (next !== committedRef.current) commit(next)
  }

  const handleSelect = (date: Date | undefined) => {
    if (!date) return
    const next = format(date, DATE_FORMAT)
    setText(next)
    commit(next)
    setOpen(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setMonth(selected ?? new Date(today.getFullYear() - DEFAULT_AGE_YEARS, 0, 1))
      setPanel(null)
    }
    setOpen(nextOpen)
  }

  const lang = i18n?.language?.startsWith('en') ? 'en' : 'ko'
  const locale = lang === 'en' ? enUS : ko
  const startMonth = new Date(today.getFullYear() - YEARS_BACK, 0, 1)
  const years = Array.from({ length: YEARS_BACK + 1 }, (_, i) => today.getFullYear() - i)

  const pickYear = (year: number) => {
    const maxMonth = year === today.getFullYear() ? today.getMonth() : 11
    setMonth(new Date(year, Math.min(month.getMonth(), maxMonth), 1))
    setPanel(null)
  }
  const pickMonth = (monthIndex: number) => {
    setMonth(new Date(month.getFullYear(), monthIndex, 1))
    setPanel(null)
  }

  const captionContext: CaptionContextValue = {
    lang,
    openPanel: setPanel,
    yearLabel: t('datePicker.selectYear'),
    monthLabelText: t('datePicker.selectMonth'),
  }

  const optionClass = (active: boolean) =>
    cn(
      'h-9 rounded-md text-sm hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent',
      active && 'bg-primary text-primary-foreground hover:bg-primary',
    )

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        required={required}
        placeholder={t('datePicker.placeholder')}
        value={text}
        onChange={(e) => handleTextChange(e.target.value)}
        className={cn('pr-11', invalid && 'border-red-500', inputClassName)}
      />
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          type="button"
          aria-label={t('datePicker.openCalendar')}
          className="absolute right-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <CalendarDays className="h-5 w-5" />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <CaptionContext.Provider value={captionContext}>
            <div className="relative">
              <Calendar
                mode="single"
                captionLayout="label"
                components={calendarComponents}
                locale={locale}
                selected={selected}
                month={month}
                onMonthChange={setMonth}
                startMonth={startMonth}
                endMonth={today}
                disabled={{ after: today }}
                onSelect={handleSelect}
              />
              {panel && (
                <div
                  role="group"
                  aria-label={panel === 'year' ? t('datePicker.selectYear') : t('datePicker.selectMonth')}
                  className="absolute inset-0 z-20 flex flex-col rounded-lg bg-popover p-2"
                >
                  <div className="mb-1 flex h-7 items-center justify-between px-1">
                    <span className="text-sm font-medium">
                      {panel === 'year' ? t('datePicker.selectYear') : t('datePicker.selectMonth')}
                    </span>
                    <button
                      type="button"
                      aria-label={t('datePicker.close')}
                      className="flex size-7 items-center justify-center rounded-md hover:bg-muted"
                      onClick={() => setPanel(null)}
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-1 overflow-y-auto">
                    {panel === 'year'
                      ? years.map((year) => (
                          <button
                            key={year}
                            type="button"
                            aria-pressed={year === month.getFullYear()}
                            className={optionClass(year === month.getFullYear())}
                            onClick={() => pickYear(year)}
                          >
                            {year}
                          </button>
                        ))
                      : Array.from({ length: 12 }, (_, m) => m).map((m) => {
                          const disabled = month.getFullYear() === today.getFullYear() && m > today.getMonth()
                          return (
                            <button
                              key={m}
                              type="button"
                              disabled={disabled}
                              aria-pressed={m === month.getMonth()}
                              className={optionClass(m === month.getMonth())}
                              onClick={() => pickMonth(m)}
                            >
                              {monthLabel(m, lang)}
                            </button>
                          )
                        })}
                  </div>
                </div>
              )}
            </div>
          </CaptionContext.Provider>
        </PopoverContent>
      </Popover>
    </div>
  )
}
